import type { AppliedVoucher } from "../components/hooks/useVoucherRedemption";
import * as z from "zod/v4";

/**
 * Zod schema for validating voucher data from URL parameters
 * This ensures the decoded data matches the expected AppliedVoucher structure
 */
const voucherSchema = z.object({
  code: z.string().min(1, "Voucher code cannot be empty"),
  amountToRedeem: z.number().min(0, "Voucher amount must be non-negative"),
});

const voucherArraySchema = z.array(voucherSchema);

/**
 * Utility functions for persisting vouchers in URL parameters
 * to survive Stripe Connect redirects.
 */

/** Encode applied vouchers as URL-safe base64 string */
export function encodeVouchersForUrl(vouchers: AppliedVoucher[]): string {
  if (vouchers.length === 0) return "";
  try {
    const jsonString = JSON.stringify(vouchers);
    return btoa(jsonString);
  } catch (error) {
    console.warn("Failed to encode vouchers for URL:", error);
    return "";
  }
}

/** Decode vouchers from URL-safe base64 string with Zod validation */
export function decodeVouchersFromUrl(encoded: string): AppliedVoucher[] {
  if (!encoded) return [];
  try {
    const jsonString = atob(encoded);
    const parsedData = JSON.parse(jsonString);

    // Validate the parsed data with Zod
    const validationResult = voucherArraySchema.safeParse(parsedData);

    if (!validationResult.success) {
      console.warn("Invalid voucher data from URL:", validationResult.error);
      return [];
    }

    return validationResult.data;
  } catch (error) {
    console.warn("Failed to decode vouchers from URL:", error);
    return [];
  }
}

/** Save vouchers to URL parameters */
export function saveVouchersToUrl(vouchers: AppliedVoucher[]): void {
  const encoded = encodeVouchersForUrl(vouchers);
  const url = new URL(window.location.href);

  if (encoded) {
    url.searchParams.set("vouchers", encoded);
  } else {
    url.searchParams.delete("vouchers");
  }

  window.history.replaceState(window.history.state, "", url.toString());
}

/** Load vouchers from URL parameters */
export function loadVouchersFromUrl(): AppliedVoucher[] {
  const url = new URL(window.location.href);
  const encoded = url.searchParams.get("vouchers");
  return decodeVouchersFromUrl(encoded ?? "");
}
